import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Users, Briefcase, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="text-center p-6 rounded-xl bg-[#111827] border border-[#00E5FF]/20">
    <Icon className="w-8 h-8 text-[#00E5FF] mx-auto mb-3" />
    <div className="text-3xl font-bold text-white font-['Space_Grotesk']">{value}</div>
    <div className="text-[#9CA3AF] text-sm mt-1">{label}</div>
  </div>
);

const StoryCard = ({ name, role, previous, program, duration, highlight, story, skills }) => (
  <Card className="bg-[#111827] border-2 border-[#00E5FF]/15 hover:border-[#00E5FF]/40 transition-all duration-300 group">
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="px-3 py-1 bg-[#1DE9B6]/10 text-[#1DE9B6] text-xs font-bold rounded-full">{highlight}</span>
        <span className="text-[#9CA3AF] text-xs">{duration}</span>
      </div>
      <h3 className="text-white font-bold text-lg mb-1 group-hover:text-[#00E5FF] transition-colors">{name}</h3>
      <p className="text-[#00E5FF] text-sm font-medium mb-1">{role}</p>
      <p className="text-[#9CA3AF] text-xs mb-4">Previously: {previous}</p>
      <p className="text-[#9CA3AF] text-sm leading-relaxed mb-4">{story}</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {skills.map((skill) => (
          <span key={skill} className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">{skill}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[#1DE9B6] text-sm pt-3 border-t border-[#00E5FF]/10">
        <CheckCircle2 className="w-4 h-4" />
        <span>Program: {program}</span>
      </div>
    </CardContent>
  </Card>
);

const SuccessStoriesPage = () => {
  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0B0F1A] to-[#020617] relative">
        <div className="absolute top-20 right-1/4 w-80 h-80 bg-[#00E5FF] rounded-full blur-[150px] opacity-10"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-['Space_Grotesk']" data-testid="success-heading">
            Success <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Stories</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">Real students. Real transformations. See how CloudXAI changed careers.</p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          <StatCard icon={Users} value="500+" label="Students Trained" />
          <StatCard icon={Briefcase} value="85%" label="Placement Rate" />
          <StatCard icon={TrendingUp} value="40%" label="Avg Salary Hike" />
          <StatCard icon={Award} value="50+" label="Hiring Partners" />
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StoryCard
            name="Rohit Verma" role="AI Engineer at Infosys" previous="BSc Graduate, Fresher"
            program="AI & Machine Learning" duration="10 Weeks" highlight="8.5 LPA Package"
            story="Rohit had no coding background. After completing the AI & ML program, he built 3 real projects including a sentiment analysis tool. Placed at Infosys within 2 weeks of course completion."
            skills={['Python', 'TensorFlow', 'NLP']}
          />
          <StoryCard
            name="Sneha Kulkarni" role="Data Engineer at TCS" previous="Manual Tester, 2 years exp"
            program="Azure Data Engineer + GenAI" duration="3 Months" highlight="12 LPA Package"
            story="Sneha transitioned from manual testing to data engineering. The Azure program with GenAI integration gave her a competitive edge. She cleared DP-203 certification and was placed at TCS."
            skills={['Azure', 'Data Factory', 'Databricks']}
          />
          <StoryCard
            name="Vikram Joshi" role="DevOps Engineer at Wipro" previous="BCA Graduate, Intern"
            program="AWS DevOps" duration="3 Months" highlight="7.5 LPA Package"
            story="Vikram went from a low-paying internship to a full-time DevOps role. The hands-on labs with real CI/CD pipelines and Kubernetes deployments made him interview-ready."
            skills={['AWS', 'Docker', 'Kubernetes']}
          />
          <StoryCard
            name="Priya Nair" role="Cybersecurity Analyst at HCL" previous="IT Support, 1 year exp"
            program="Cyber Security" duration="2 Months" highlight="9 LPA Package"
            story="Priya upskilled from basic IT support to a specialized cybersecurity role. The SOC analyst training and hands-on pentesting labs were game-changers for her career."
            skills={['Ethical Hacking', 'SIEM', 'SOC']}
          />
          <StoryCard
            name="Arjun Mehta" role="Data Analyst at Accenture" previous="Commerce Graduate"
            program="Python & SQL Foundation" duration="2 Months" highlight="6 LPA Package"
            story="Coming from a commerce background, Arjun had zero tech skills. The Python & SQL foundation program gave him the confidence to crack data analyst interviews."
            skills={['Python', 'SQL', 'Data Analysis']}
          />
          <StoryCard
            name="Kavitha R." role="Data Engineer at Cognizant" previous="Fresher, MTech Graduate"
            program="Snowflake Data Engineering" duration="2 Months" highlight="10 LPA Package"
            story="Kavitha leveraged her academic knowledge with practical Snowflake skills. The ETL pipeline projects and certification prep helped her stand out in campus placements."
            skills={['Snowflake', 'ETL', 'dbt']}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 font-['Space_Grotesk'] text-white">Your Success Story Starts Here</h2>
          <p className="text-[#9CA3AF] mb-8">Join 500+ professionals who transformed their careers with CloudXAI.</p>
          <Link to="/book-a-consultation">
            <Button className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-base rounded-lg shadow-lg shadow-[#00E5FF]/40">
              Start Your Journey <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SuccessStoriesPage;
